import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { BUTTON_TYPES, ICON_TYPES } from '../../../shared/constants';

import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import mq from '../../../styles/breakpoints';
import { Body, H2 } from '../../../styles/generic';

import StaticContent from '../../../data/static-content/magazine/static-content.json';
import InternetIcon from '../../../images/pr-internet.svg';

import { MediaProps } from './Media.types';

const TYPES = [
  { name: 'linkedin', type: 'LinkedIn-Video' },
  { name: 'youtube', type: 'Youtube-Embed' },
  { name: 'audioboom', type: 'Audioboom-Podcast' },
];

const Media = ({ src, type, className }: MediaProps) => {
  const videoEmbedText = StaticContent.magazine.article.videoEmbed;
  const linkedInPlaceholderText = videoEmbedText.linkedIn.placeholder;
  const mediaElement = '';
  const [videoType, setVideoType] = useState(type);

  useEffect(() => {
    const foundType = TYPES.find(({ name }) => src.includes(name));
    return foundType
      ? setVideoType(foundType.type)
      : console.warn('Video not found');
  }, [type]);

  if (videoType === 'Audioboom-Podcast') {
    return (
      <WhiteBorderContainer>
        <iframe
          width="100%"
          height="300"
          src={src}
          title="ABN podcast player"
        />
      </WhiteBorderContainer>
    );
  }

  if (videoType === 'LinkedIn-Video') {
    return (
      <WhiteBorderContainer>
        <FallbackContainer>
          <ImageTextContainer>
            <Image src={InternetIcon} alt="abnamro-internet-icon" />
            <TextWrapper>
              <Title>{linkedInPlaceholderText.title}</Title>
              <StyledBody>{linkedInPlaceholderText.description}</StyledBody>
              <SecondaryButton
                to={src}
                className="secondary-button"
                deqComponentTitle={type}
                deqCustomAttributes={{
                  componentType: 'video',
                  elementTarget: `${src}`,
                  elementType: `${type}-link`,
                }}
                icon={ICON_TYPES.default}
                iconPosition="end"
                label={linkedInPlaceholderText.linkText}
                type={BUTTON_TYPES.external}
              />
            </TextWrapper>
          </ImageTextContainer>
        </FallbackContainer>
      </WhiteBorderContainer>
    );
  }

  if (videoType === 'Youtube-Embed') {
    return (
      <YTWrapper>
        <IframeYoutube
          id="ytplayer"
          title="YouTube video player"
          src={src}
          type="text/html"
          position="absolute"
          width="100%"
          height="100%"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </YTWrapper>
    );
  }

  return (
    <WhiteBorderContainer className={className}>
      {mediaElement}
    </WhiteBorderContainer>
  );
};

// Keep 16/9 ration also when smaller, see explanation:
// https://kevinsimper.medium.com/full-width-youtube-embed-with-react-js-responsive-embed-509de7e7c3bf
const YTWrapper = styled.div`
  padding-bottom: 56.25%;
  position: relative; /* 16:9 */
`;

const IframeYoutube = styled.iframe`
  border: 0;
  left: 0;
  position: absolute;
  top: 0;
`;

const WhiteBorderContainer = styled.div`
  background-color: white;
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  padding: 16px;

  ${mq.from.breakpoint.M`
    padding: 32px;
  `}
`;

const FallbackContainer = styled.div`
  display: block;
  height: auto;
  position: relative;
  width: 100%;

  ${mq.from.breakpoint.M`
    height: 0;
    padding-bottom: 56.25%;
  `}
`;

const Image = styled.img`
  display: block;
  height: 84px;
  width: 84px;

  ${mq.from.breakpoint.M`
    display: block;
    margin-right: 0;
    width: 84px;
    height: 84px;
  `}

  ${mq.from.breakpoint.L`
    display: block;
    margin-right: 32px;
    width: 124px;
    height: 124px;
  `}
`;

const ImageTextContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  ${mq.from.breakpoint.M`
    position: absolute;
    top: 50%;
    left: initial;
    transform: translate(0, -50%);
  `}

  ${mq.from.breakpoint.L`
    left: 50%;
    transform: translate(-50%, -50%);
    flex-direction: row;
    width: 80%;
  `}
`;

const TextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;

  ${mq.from.breakpoint.L`
    text-align: left;
    align-items: flex-start;
  `}
`;

const Title = styled(H2)`
  font-size: 20px;
`;

const StyledBody = styled(Body)`
  margin-bottom: 22px;
  text-align: center;

  ${mq.from.breakpoint.M`
    margin-bottom: 16px;
  `}

  ${mq.from.breakpoint.L`
    text-align: left;
  `}
`;

export default Media;
